import { Box, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import NavigationDrawer from "../../components/drawer/NavigationDrawer";
import { NAVIGATION_DRAWER_TYPE_HR } from "../../components/drawer/NavigationDrawer/NavigationDrawer";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import BoxWithTypography from "../../components/box/BoxWithTypography";
import FilterTable from "../../components/table/FilterTable";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import useHRService from "../../services/hrService";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import useFilterSearchParams from "../../hooks/useFilterSearchParams";
import useLocalData from "../../hooks/useLocalData";
import TableService from "../../services/tableService";
import { useEmptyArrayMemo } from "../../hooks/useEmptyArrayMemo";
import {
  MY_OPEN_SURVEY_TABLE_FIELDS_CONFIG,
  MY_CLOSED_SURVEY_TABLE_FIELDS_CONFIG,
  MY_OPEN_SURVEY_FILTER_PREFIX,
  MY_CLOSED_SURVEY_FILTER_PREFIX,
} from "./PageTablesConfig";
import { useAsync, useAsyncFn } from "../../hooks/useAsync";
import AnswerForm from "../../components/form/AnswerForm";
import { use } from "react";
import { useCrudFn } from "../../hooks/useCrudFn";
import useDialog from "../../hooks/useDialog";
import { useSnackbarAlert } from "../../context/snackbarAlert";
import ConfirmationDialog from "../../components/dialog/ConfirmationDialog";
import { isEmptyArray } from "../../helpers/methods";

export default function HrMySurveyPage(props) {
  const { t } = useTranslation();

  const { pageName } = props;
  const snackbarAlert = useSnackbarAlert();

  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedSurveyId, setSelectedSurveyId] = useState(
    () => searchParams.get("selected_id") ?? undefined
  );
  const [localAnswer, setLocalAnswer] = useState(undefined)

  const answerFormRef = useRef();

  const handleClickSurveyRow = useCallback((id) => {
    setSelectedSurveyId((prevId) => (prevId === id ? undefined : id));
    setLocalAnswer(undefined)
  }, []);

  const { emptyArrayMemo } = useEmptyArrayMemo();

  const { getMyOpenSurveys, getMyClosedSurveys, getMySurveyFilteringData, getMySurveyDetails, updateAnswer, sendMySurvey
  } = useHRService();

  const [openConfirmationDialog, onOpenConfirmationDialog, onCloseConfirmationDialog] = useDialog();


  const mySurveyFilteringData = useAsync(getMySurveyFilteringData);
  const sendMySurveyFn = useAsyncFn(sendMySurvey);

  const selectedSurveyData = useAsync(() => {
    if (selectedSurveyId) {
      return getMySurveyDetails(selectedSurveyId)
        .catch(() => {
          snackbarAlert.openErrorSnackbarAlert(
            t("snackbar_alert.you_have_not_permissions_to_see_this_survey")
          );
          setSelectedSurveyId(undefined)
          setSearchParams((prev) => {
            const newSearchParams = new URLSearchParams(prev);
            newSearchParams.delete("selected_id");
            return newSearchParams;
          });
        });
}
return Promise.resolve(undefined)
  }, [selectedSurveyId])



const answerCrudFn = useCrudFn(null, updateAnswer, null, "hr_answer");

useEffect(() => {
  if (selectedSurveyData.selectedSurveyData) return
  setLocalAnswer(selectedSurveyData.value)
}, [selectedSurveyData.loading]);

const {
  page: openSurveyPage,
  pageSize: openSurveyPageSize,
  handleChangePageWithSearchParams: handleChangeOpenSurveyPage,
  handleChangePageSizeWithSearchParams: handleChangeOpenSurveyPageSize,
  resetPageNumber: resetOpenSurveyPageNumber,
  searchParams: openSurveyRawSearchParams,
} = usePaginationWithSearchParams(MY_OPEN_SURVEY_FILTER_PREFIX);

const {
  page: closedSurveyPage,
  pageSize: closedSurveyPageSize,
  handleChangePageWithSearchParams: handleChangeClosedSurveyPage,
  handleChangePageSizeWithSearchParams: handleChangeClosedSurveyPageSize,
  resetPageNumber: resetClosedSurveyPageNumber,
  searchParams: closedSurveyRawSearchParams,
} = usePaginationWithSearchParams(MY_CLOSED_SURVEY_FILTER_PREFIX);

const {
  setNewItemSearchParamsIfAreChanged: setNewOpenSurveyParamsIfAreChanged,
  getSearchParamsByFilterPrefix: getOpenSurveyParamsByFilterPrefix,
} = useFilterSearchParams(openSurveyRawSearchParams);

const [openSurveySearchParams, setOpenSurveySearchParams] = useState(
  getOpenSurveyParamsByFilterPrefix(MY_OPEN_SURVEY_FILTER_PREFIX)
);

useEffect(() => {
  setNewOpenSurveyParamsIfAreChanged(
    MY_OPEN_SURVEY_FILTER_PREFIX,
    openSurveySearchParams,
    setOpenSurveySearchParams
  );
}, [openSurveyRawSearchParams]);

const {
  setNewItemSearchParamsIfAreChanged: setNewClosedSurveyParamsIfAreChanged,
  getSearchParamsByFilterPrefix: getClosedSurveyParamsByFilterPrefix,
} = useFilterSearchParams(closedSurveyRawSearchParams);

const [closedSurveySearchParams, setClosedSurveySearchParams] = useState(
  getClosedSurveyParamsByFilterPrefix(MY_CLOSED_SURVEY_FILTER_PREFIX)
);

useEffect(() => {
  setNewClosedSurveyParamsIfAreChanged(
    MY_CLOSED_SURVEY_FILTER_PREFIX,
    closedSurveySearchParams,
    setClosedSurveySearchParams
  );
}, [closedSurveyRawSearchParams]);

const {
  dataCount: openSurveyDataCount,
  dataRefetch: openDataRetch,
  dataLoading: openSurveyDataLoading,
  tableData: openSurveyTableData,
} = useLocalData(
  getMyOpenSurveys,
  openSurveySearchParams,
  true,
  undefined,
  MY_OPEN_SURVEY_TABLE_FIELDS_CONFIG
);

const {
  dataCount: closedSurveyDataCount,
  dataRefetch: closedDataRetch,
  dataLoading: closedSurveyDataLoading,
  tableData: closedSurveyTableData,
} = useLocalData(
  getMyClosedSurveys,
  closedSurveySearchParams,
  true,
  undefined,
  MY_CLOSED_SURVEY_TABLE_FIELDS_CONFIG
);

const handleChangeAnswer = useCallback((answerId, dataToSend) => {
  answerCrudFn.onUpdateObject(answerId, dataToSend, () => undefined, selectedSurveyData.refetch)
}, [])

const handleClickSendSurvey = useCallback(() => {
  let notFilledArray = answerFormRef.current?.checkIfRequiredFieldsAreFill() || []
  if (selectedSurveyId && !isEmptyArray(notFilledArray)) {
    onOpenConfirmationDialog()

  } else {
    handleSendSurvey()
  }
}, [selectedSurveyId, selectedSurveyData])

const handleCloseConfirmationDialog = useCallback((confirmSend) => {
  onCloseConfirmationDialog()
  if (confirmSend) {
    handleSendSurvey()
  }
}, [selectedSurveyId])

const handleSendSurvey = useCallback(() => {
  sendMySurveyFn.execute(selectedSurveyId)
    .then(() => {
      selectedSurveyData.refetch()
      closedDataRetch()
      openDataRetch()
      snackbarAlert.openSuccessSnackbarAlert(
        t("snackbar_alert.survey_sended")
      );
    })
    .catch(() => {
      snackbarAlert.openErrorSnackbarAlert(
        t("snackbar_alert.occurred_error_during_survey_sending")
      );
    });
}, [selectedSurveyId])

return (
  <NavigationDrawer
    drawerType={NAVIGATION_DRAWER_TYPE_HR}
    pageName={pageName}
  >
    <DefaultPageWrapper titleKey={pageName}>
      
          <LoaderWrapper showLoader={openSurveyDataLoading && closedSurveyDataLoading}>
      <Grid
        container
        item
        direction="row"
        justifyContent="flex-start"
        alignItems="stretch"
        rowSpacing={1}
        columnSpacing={1}
      >
        <Grid item xs={12} md={4.5}>
          <LoaderWrapper showLoader={openSurveyDataLoading || closedSurveyDataLoading}>
            <Grid container item direction="column" rowSpacing={1}>
              <Grid item xs={12}>
                <BoxWithTypography
                  label={t("page.hr_my_survey_page.active_surveys")}
                >
                  <LoaderWrapper showLoader={openSurveyTableData === undefined}>
                    <FilterTable
                      data={openSurveyTableData ?? emptyArrayMemo}
                      headersConfig={TableService.getHeadersConfigForCollapsibleTable(
                        MY_OPEN_SURVEY_TABLE_FIELDS_CONFIG,
                        mySurveyFilteringData
                      )}
                      filterPrefix={MY_OPEN_SURVEY_FILTER_PREFIX}
                      showCheckbox={false}
                      showCleanFilterIcon={true}
                      countRecords={openSurveyDataCount}
                      page={openSurveyPage}
                      onPageChange={handleChangeOpenSurveyPage}
                      rowsPerPage={openSurveyPageSize}
                      onRowsPerPageChange={handleChangeOpenSurveyPageSize}
                      resetPageNumber={resetOpenSurveyPageNumber}
                      onClickRow={handleClickSurveyRow}
                      selectedItemId={selectedSurveyId}
                      isLoading={openSurveyDataLoading}
                    />
                  </LoaderWrapper>
                </BoxWithTypography>
              </Grid>
              <Grid item xs={12}>
                <BoxWithTypography
                  label={t("page.hr_my_survey_page.closed_surveys")}
                >
                  <LoaderWrapper showLoader={closedSurveyTableData === undefined}>
                    <FilterTable
                      data={closedSurveyTableData ?? emptyArrayMemo}
                      headersConfig={TableService.getHeadersConfigForCollapsibleTable(
                        MY_CLOSED_SURVEY_TABLE_FIELDS_CONFIG,
                        mySurveyFilteringData
                      )}
                      filterPrefix={MY_CLOSED_SURVEY_FILTER_PREFIX}
                      showCheckbox={false}
                      showCleanFilterIcon={true}
                      countRecords={closedSurveyDataCount}
                      page={closedSurveyPage}
                      onPageChange={handleChangeClosedSurveyPage}
                      rowsPerPage={closedSurveyPageSize}
                      onRowsPerPageChange={handleChangeClosedSurveyPageSize}
                      resetPageNumber={resetClosedSurveyPageNumber}
                      onClickRow={handleClickSurveyRow}
                      selectedItemId={selectedSurveyId}
                      isLoading={closedSurveyDataLoading}
                    />
                  </LoaderWrapper>
                </BoxWithTypography>
              </Grid>
            </Grid>
          </LoaderWrapper>
        </Grid>
        <Grid item xs={12} md={7.5}>
          <BoxWithTypography
            label={t("page.hr_my_survey_page.survey_details")}
          >
            {selectedSurveyId === undefined
              ?
              <Typography
                textAlign="center"
                variant="overline"
                display="block"
                gutterBottom
                style={{ margin: 0, fontSize: "20px", color: "var(--primary)" }}
              >
                {t("page.hr_my_survey_page.select_survey_placeholder")}
              </Typography>


              : <LoaderWrapper showLoader={localAnswer === undefined}>
                <Box sx={{ maxHeight: "80vh", overflowY: "auto", paddingTop: 3 }}>
                  <AnswerForm
                    ref={answerFormRef}
                    surveyDetails={selectedSurveyData.value}
                    readOnly={selectedSurveyData?.value?.is_read_only || sendMySurveyFn.loading || answerCrudFn.isUpdatingLoading}
                    onEditAnswer={handleChangeAnswer}
                    onSendSurvey={handleClickSendSurvey}
                    isSaving={sendMySurveyFn.loading || answerCrudFn.isUpdatingLoading}
                    isSendingSurvey={sendMySurveyFn.loading}
                  />
                </Box>
              </LoaderWrapper>
            }
          </BoxWithTypography>
        </Grid>
      </Grid>

      {openConfirmationDialog && (
        <ConfirmationDialog
          open={openConfirmationDialog}
          onNo={() => handleCloseConfirmationDialog(false)}
          onYes={() => handleCloseConfirmationDialog(true)}
          title={t("page.hr_my_survey_page.send_not_fullfilled_survey")}
          content={t("page.hr_my_survey_page.are_you_sure_to_send_not_fullfilled_survey")}
        />
      )}
      </LoaderWrapper>
    </DefaultPageWrapper>
  </NavigationDrawer>
);
}
